import {Injectable} from '@angular/core';
import {NativeDateAdapter} from '@angular/material/core';

const siteTimeZone = 'Europe/Berlin';

@Injectable({providedIn: 'root'})
export class CustomDateAdapter extends NativeDateAdapter {

    constructor(
    ) {
        super();
    }

    getFirstDayOfWeek(): number {
        return 1;
    }

    parse(value: any): Date | null {
        const split = value.split('.');
        let toDate = new Date(value);

        if (split.length > 2) {
            toDate = new Date(split[2], split[1] - 1, split[0]);
        }
        return toDate;
    }
}


export function isSameMonth(date1: Date, date2: Date) {
    return date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
}

export function isSameDayOccasion(number1: number, number2: Date) {
    if (number1 == null || number2 == null) {
        return false;
    }

    const date1 = new Date(number1);
    const date2 = new Date(number2);

    return date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();
}

export function isSameDay(date1: Date, date2: Date) {
    if (date1 == null || date2 == null) {
        return false;
    }

    const x = new Date(date1);
    const y = new Date(date2);

    return x.getFullYear() === y.getFullYear() &&
        x.getMonth() === y.getMonth() &&
        x.getDate() === y.getDate();
}

export function addDays(date: Date, days: number) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

export function addMonths(date: Date, months: number) {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

export function getSiteDateKey(date = new Date(), addSiteDays = 0): string {
    const siteDate = addSiteDays === 0 ? date : getSiteDateWithOffset(date, addSiteDays);
    const parts = getSiteDateParts(siteDate);

    return `${parts.year}-${parts.month}-${parts.day}`;
}

function getSiteDateWithOffset(date: Date, days: number): Date {
    const parts = getSiteDateParts(date);

    // Noon UTC keeps the target date stable across timezone and DST boundaries.
    return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day) + days, 12));
}

function getSiteDateParts(date: Date) {
    const parts = new Intl.DateTimeFormat('en', {
        timeZone: siteTimeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(date);

    return Object.fromEntries(parts.map(part => [part.type, part.value]));
}

/**
 * This fills the days array with the previous and next days to get a full calendar view
 * @param month
 */
export function getDaysForMonthFill(month: Date): Date[][] {
    let result: Date[][] = [];

    const monthStart = getMonthStart(month);
    const monthEnd = getMonthEnd(month);

    let current = new Date(monthStart);

    while (current < monthEnd) {
        const week = dates(current);
        result.push(week);

        current = addDays(current, 7);
    }

    let containsFirst = false;
    let containsLast = false;
    result.forEach(week => {
        const foundFirst = week.find(day => isSameDay(day, monthStart));
        const foundLast = week.find(day => isSameDay(day, monthEnd));
        if (foundFirst) {
            containsFirst = true;
        }
        if (foundLast) {
            containsLast = true;
        }
    });

    if (!containsFirst) {
        const previousWeek = addDays(monthStart, -7);
        const week = dates(previousWeek);
        result.push(week);
    }

    if (!containsLast) {
        const nextWeek = addDays(monthEnd, 1);
        const week = dates(nextWeek);
        result.push(week);
    }

    result = result.sort((a, b) => {
        const firstDayA = a[0].getTime();
        const firstDayB = b[0].getTime();
        return firstDayA - firstDayB;
    });

    return result;
}

export function getMonthStart(date: Date): Date {
    const month = new Date(date);
    return new Date(month.getFullYear(), month.getMonth(), 1);
}

export function getMonthEnd(date: Date): Date {
    const month = new Date(date);
    return new Date(month.getFullYear(), month.getMonth() + 1, 0);
}


function dates(weekDay: Date): Date[] {
    const week = [];
    const current = new Date(weekDay);
    current.setDate((current.getDate() - current.getDay() + 1));

    for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }

    return week;
}

